import React, { useEffect, useMemo, useState } from 'react';
import { useV3Api } from '../../api/useV3Api';
import { errorText } from '../../api/errorText';
import Portal from '../Portal';
import Bear from '../Bear';
import { filterQuery, ruleFor, switchesOf, tickSwitches, withRule } from '../../policy/ruleSwitches';

const SWITCHES = [['install', 'Install'], ['update', 'Update'], ['remove', 'Remove']];
const list = (v) => (Array.isArray(v) ? v : []);

// The blade on the right of a software policy: software to put in its rules. Ticked, it lists what is installed on the machines the policy applies
// to (GET v3/software); cleared, it searches the winget catalog (GET v3/packages). Each line has the rule's Install, Update and Remove switches,
// the same ones as the rule list. A tick changes the rules being edited at once; they are saved with the policy.
export default function AddSoftwareBlade({ account, filter, rules, onChange, onClose }) {
  const { get } = useV3Api();
  const [text, setText] = useState('');
  const global = account === '*';                       // a global policy has no machines of its own: only the catalog
  const [installedOnly, setInstalledOnly] = useState(!global);
  const [hideUnnamed, setHideUnnamed] = useState(true);       // software with no winget id (or definition) cannot be put in a rule: hidden unless asked for
  const [installed, setInstalled] = useState({ state: 'loading', items: [] });
  const [found, setFound] = useState({ state: 'idle', items: [] });
  const query = filterQuery(filter);

  useEffect(() => {
    if (global) return undefined;
    let cancelled = false;
    setInstalled({ state: 'loading', items: [] });
    get('v3/software', { account, filter: query })
      .then((r) => { if (!cancelled) setInstalled({ state: 'ok', items: list(r && r.items), machineCount: r && r.machineCount }); })
      .catch((ex) => { if (!cancelled) setInstalled({ state: 'error', items: [], error: errorText(ex) }); });
    return () => { cancelled = true; };
  }, [get, account, query, global]);

  useEffect(() => {
    const q = text.trim();
    if (installedOnly || q.length < 2) { setFound({ state: 'idle', items: [] }); return undefined; }
    let cancelled = false;
    setFound((f) => ({ ...f, state: 'loading' }));
    const timer = setTimeout(() => {
      get('v3/packages', { q })
        .then((r) => { if (!cancelled) setFound({ state: 'ok', items: list(r && r.items) }); })
        .catch((ex) => { if (!cancelled) setFound({ state: 'error', items: [], error: errorText(ex) }); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [get, text, installedOnly]);

  const words = text.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const rows = useMemo(() => {
    if (!installedOnly) return found.items.map((p) => ({ key: p.id, name: p.name, detail: p.id, newest: p.latestVersion, addable: true }));
    return installed.items
      .filter((p) => !hideUnnamed || p.addable)
      .filter((p) => words.every((w) => `${p.key || ''} ${p.name} ${p.wingetId || ''}`.toLowerCase().includes(w)))
      .map((p) => ({ key: p.key, name: p.name, detail: p.key || '', newest: p.newest, machines: p.machines, addable: p.addable }));
  }, [installedOnly, hideUnnamed, found.items, installed.items, text]); // eslint-disable-line react-hooks/exhaustive-deps
  const unnamed = installed.items.filter((p) => !p.addable).length;
  const loading = installedOnly ? installed.state === 'loading' : found.state === 'loading';
  const problem = installedOnly ? installed.error : found.error;
  const ready = installedOnly ? installed.state === 'ok' : found.state === 'ok';

  return (
    <Portal>
      <div className="blade-scrim" onClick={onClose} role="presentation" />
      <aside className="blade" role="dialog" aria-label="Add software">
        <header>
          <Bear name="kongi" size={44} />
          <div className="blade-title">
            <h2>Add software</h2>
            <div className="sub">{global ? 'For every account' : query ? `For machines where ${query.split(';').join(', ').split('=').join(' is ')}` : 'For every machine of the account'}</div>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">Close</button>
        </header>
        <div className="blade-body">
          <input type="search" aria-label="Search software" placeholder="Search by name or keyword, for example storage explorer" value={text} onChange={(e) => setText(e.target.value)} />
          {!global && <label className="check"><input type="checkbox" checked={installedOnly} onChange={(e) => setInstalledOnly(e.target.checked)} />Only software installed on these machines</label>}
          {!global && installedOnly && (
            <label className="check"><input type="checkbox" checked={hideUnnamed} onChange={(e) => setHideUnnamed(e.target.checked)} />{`Hide software with no winget id${unnamed ? ` (${unnamed})` : ''}`}</label>
          )}
          <div className="help">Ticks change the rules on the page. Save the policy to keep them.</div>
          {loading && <div className="help" role="status">Loading…</div>}
          {problem && <div className="field-error" role="alert">{problem}</div>}
          {!installedOnly && found.state === 'idle' && <div className="help">Type at least two characters to search.</div>}
          {rows.length > 0 && (
            <table aria-label="Software">
              <thead><tr><th>Software</th><th>Newest</th>{SWITCHES.map(([name, label]) => <th key={name}>{label}</th>)}</tr></thead>
              <tbody>
                {rows.map((row) => {
                  const rule = ruleFor(rules, row.key);
                  const on = switchesOf(rule);
                  return (
                    <tr key={row.key || row.name}>
                      <td>
                        <strong>{row.name}</strong>
                        <div className="help">{row.addable ? row.detail : 'No winget id, so a rule cannot name it yet.'}{row.machines ? ` · ${row.machines} machine${row.machines === 1 ? '' : 's'}` : ''}</div>
                      </td>
                      <td>{row.newest || ''}</td>
                      {SWITCHES.map(([name, label]) => (
                        <td key={name}>
                          <input type="checkbox" aria-label={`${label} ${row.name}`} disabled={!row.addable} checked={on[name]}
                            onChange={(e) => onChange(withRule(rules, row.key, tickSwitches(name, e.target.checked)))} />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {ready && rows.length === 0 && <div className="help">No software matches.</div>}
        </div>
      </aside>
    </Portal>
  );
}
